import React, { useRef, useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import classes from "./CheckListViewRow.scss";
import { colors, fontStyle } from "Constants";
import classNames from "classnames";
import { getCountString } from "Utils";
import I18nHOC from "UIComponents/I18nHOC";
import { TickCircleFilled, CircleOutlined } from "@toddle-edu/ds-icons";
import { getUserInfo } from "Login/modules/LoginModule";
import { getStudentDetailsWithArchivedStatus } from "ClassRoom/modules/utils";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import StudentListPopover from "UIComponents/StudentListPopover";

const inputStyle = {
  cell: {
    display: "flex",
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    width: "100%",
    color: colors.neutral200,
    lineHeight: 1.57,
    alignItems: "center",
    zIndex: 1,
    ...fontStyle.medium,
  },
  disbledCell: {
    color: colors.surfaceDisabled,
  },
  cellWithIcon: {
    flexShrink: 1,
  },
};

const styles = {
  headerStyle: {
    backgroundColor: "var(--surface-tertiary-enabled)",
  },
  aiTick: {
    color: "var(--interactive-tertiary)",
  },
};

const CheckListOption = ({
  label,
  id,
  rowId,
  toolEvaluationResponses,
  isEvaluation = false,
  updateEvaluationCell,
  isEvaluatedByStudent,
  showInsight,
  insights,
  insightColor,
  userType,
  t,
  toolStyles,
  evaluationCellRefs,
  rowIndex,
  index,
  isBottomRightCell,
  showRevampedEvaluationUI,
  isSharedWithStudent,
  showOnlyDefaultCursor,
  onAcceptAIEvaluation,
  isAiEvaluationEnabled,
  isGeneratingAIResponse,
}) => {
  const [showPopover, setShowPopover] = useState(false);

  const containerRef = useRef(null);

  const groupedToolEvaluationResponse = _.groupBy(
    toolEvaluationResponses,
    toolEvaluationResponse => toolEvaluationResponse.creatorType
  );

  const staffToolEvaluationResponses = _.get(
    groupedToolEvaluationResponse,
    "STAFF",
    []
  );

  const studentToolEvaluationResponses = _.get(
    groupedToolEvaluationResponse,
    "STUDENT",
    []
  );

  const aiToolEvaluationResponses = _.get(
    groupedToolEvaluationResponse,
    "AI",
    []
  );

  const isRowEvaluatedByTeacher = _.some(
    staffToolEvaluationResponses,
    response => response?.rowId == rowId
  );

  const responseCount = _.get(
    _.find(
      insights,
      insight => insight.rowId == rowId && insight.columnId == id
    ),
    "responseCount",
    0
  );

  let isAISelected =
    _.findIndex(
      aiToolEvaluationResponses,
      response =>
        response.rowId == rowId &&
        response.columnId == id &&
        !response.isDeleted
    ) >= 0;

  let isSelected =
    _.findIndex(
      staffToolEvaluationResponses,
      response => response.rowId == rowId && response.columnId == id
    ) >= 0;

  // TODO: Remove later, Temp code to handle AI evaluation for student
  // - For student we need to present the AI evaluation as if it were the teacher's evaluation
  // - A single row can contain both teacher and AI evaluations (if teacher updated the evaluation after AI)
  //   in that case, we need to show the teacher's evaluation
  if (userType !== "staff") {
    // Hide AI selection if teacher evaluated or content not shared with student
    if (isRowEvaluatedByTeacher || !isSharedWithStudent) {
      isAISelected = false;
    }
    // Show AI evaluation as teacher evaluation for students when appropriate
    else if (!isSelected && isAISelected && isSharedWithStudent) {
      isSelected = true;
      isAISelected = false;
    }
  }

  const isStudentSelected =
    _.findIndex(
      studentToolEvaluationResponses,
      response => response.rowId == rowId && response.columnId == id
    ) >= 0 && isEvaluatedByStudent;

  const cellClass = classNames(
    { [classes.option]: true },
    { [classes.clickableCell]: isEvaluation && userType == "staff" },
    {
      [classes.clickableStudentCell]:
        isEvaluation && userType == "student" && !showRevampedEvaluationUI,
      [classes.clickableStudentCellV2]:
        isEvaluation && userType == "student" && showRevampedEvaluationUI,
    },
    {
      [classes.aiSelectableCell]: isAISelected && !isSelected,
    },
    {
      [classes.selectableCell]:
        isSelected &&
        !showRevampedEvaluationUI &&
        (isAiEvaluationEnabled ? !isAISelected : true),
      [classes.selectableCellV2]:
        isSelected &&
        showRevampedEvaluationUI &&
        (isAiEvaluationEnabled ? !isAISelected : true),
    },
    {
      [classes.selectableStudentCell]:
        !isSelected && isStudentSelected && !showRevampedEvaluationUI,
      [classes.selectedStudentEvaluationOpenCell]:
        !isSelected &&
        isStudentSelected &&
        showRevampedEvaluationUI &&
        isEvaluation,
      [classes.selectedStudentEvaluationClosedCell]:
        !isSelected &&
        isStudentSelected &&
        showRevampedEvaluationUI &&
        !isEvaluation,
    },
    {
      [classes.insightCell]: showInsight,
    },
    {
      [classes.cursorNotAllowed]: !isEvaluation && !showOnlyDefaultCursor,
      [classes.defaultCursor]: !isEvaluation && showOnlyDefaultCursor,
    }
  );

  const studentArray = _.get(
    _.find(
      insights,
      insight =>
        _.isEqual(_.get(insight, "rowId", ""), rowId) &&
        _.isEqual(_.get(insight, "columnId", ""), id)
    ),
    "studentsList",
    []
  );

  const handleOnHover = async () => {
    if (_.isEmpty(studentArray)) return;
    setShowPopover(true);
  };

  const handleOnHoverOut = () => {
    setShowPopover(false);
  };

  const moreComponent = (
    <div
      ref={containerRef}
      onMouseEnter={handleOnHover}
      onMouseLeave={handleOnHoverOut}
      className={classes.insight}
    >
      <div className={classes.insightCount} style={{ color: insightColor }}>
        {responseCount}
      </div>
      <div className={classes.insightStudent}>
        {getCountString({
          count: responseCount,
          singularText: t("common:student").toLowerCase(),
          pluralText: t("common:student_plural").toLowerCase(),
          shouldShowZero: true,
          showCount: false,
        })}
      </div>
      {showPopover ? (
        <StudentListPopover
          studentList={getStudentDetailsWithArchivedStatus({
            studentArray,
          })}
          popOverContainerClasses={classes.popoverContainer}
          containerRef={containerRef}
          position={["top"]}
        />
      ) : null}
    </div>
  );

  const { optionStyle, bottomRightCellStyle = {} } = toolStyles || {};

  const updateCellRef = ref => {
    evaluationCellRefs[`${rowIndex}_${index}`] = ref;
  };

  const handleClickEvaluation = () => {
    const visibleAIResponse =
      isAiEvaluationEnabled &&
      userType == "staff" &&
      _.find(
        aiToolEvaluationResponses,
        response => response.rowId === rowId && !response.isDeleted
      );

    const columnId =
      (userType == "staff" && isSelected) ||
      (userType == "student" && isStudentSelected)
        ? null
        : id;

    if (isEvaluation) {
      const isDeleteAiRatings =
        !_.isEmpty(columnId) && !_.isEmpty(visibleAIResponse);

      updateEvaluationCell({
        rowId: rowId,
        columnId,
        isDeleteAiRatings,
      });

      if (isDeleteAiRatings) {
        onAcceptAIEvaluation({
          rowId,
          columnId: id,
        });
      }
    }
  };

  const handleKeyDown = e => {
    if (!isEvaluation) {
      return;
    }

    switch (e?.key) {
      case "Enter":
      case " ":
        e?.preventDefault();
        handleClickEvaluation();
        break;
      case "ArrowRight":
        e?.preventDefault();
        evaluationCellRefs[`${rowIndex}_${index + 1}`]?.focus();
        break;
      case "ArrowLeft":
        e?.preventDefault();
        evaluationCellRefs[`${rowIndex}_${index - 1}`]?.focus();
        break;
      case "ArrowDown":
        e?.preventDefault();
        evaluationCellRefs[`${rowIndex + 1}_${index}`]?.focus();
        break;
      case "ArrowUp":
        e?.preventDefault();
        evaluationCellRefs[`${rowIndex - 1}_${index}`]?.focus();
        break;
    }
  };

  return isGeneratingAIResponse ? (
    <SkeletonLoader width="100%" height="100%" />
  ) : (
    <div
      tabIndex={isEvaluation ? 0 : null}
      onClick={handleClickEvaluation}
      onKeyDown={handleKeyDown}
      className={cellClass}
      style={{
        ...optionStyle,
        ...(isBottomRightCell ? bottomRightCellStyle : {}),
      }}
      ref={updateCellRef}
      role={"button"}
    >
      {!showInsight ? (
        showRevampedEvaluationUI ? (
          <div className={classes.labelWithIconContainer}>
            <div style={{ ...inputStyle.cell, ...inputStyle.cellWithIcon }}>
              {label}
            </div>
            {isStudentSelected || isSelected ? (
              <div className={classes.tickIconContainer}>
                {isStudentSelected && (
                  <TickCircleFilled
                    size={isEvaluation ? "xx-small" : "xxx-small"}
                  />
                )}
                {isSelected && (
                  <TickCircleFilled
                    size={isEvaluation ? "xx-small" : "xxx-small"}
                    variant={"link"}
                  />
                )}
              </div>
            ) : isEvaluation ? (
              <CircleOutlined size={"x-small"} variant="subtle" />
            ) : null}
          </div>
        ) : (
          <>
            <div style={inputStyle.cell}>{label}</div>
            {isAISelected ? (
              <div className={classes.aiSelectableCell2} />
            ) : null}
            {isSelected && (isAiEvaluationEnabled ? !isAISelected : true) ? (
              <div className={classes.selectableCell2} />
            ) : null}
            {isStudentSelected || isSelected || isAISelected ? (
              <div className={classes.tickSvgContainer}>
                {isStudentSelected && (
                  <div
                    className={classes.tickSvgComponent}
                    style={{ marginRight: isSelected ? "8px" : "0px" }}
                  >
                    <TickCircleFilled size={"xx-small"} />
                  </div>
                )}

                {isSelected &&
                (isAiEvaluationEnabled ? !isAISelected : true) ? (
                  <div className={classes.tickSvgComponent}>
                    <TickCircleFilled size={"xx-small"} variant={"link"} />
                  </div>
                ) : isAISelected ? (
                  <div className={classes.tickSvgComponent}>
                    <TickCircleFilled size={"xx-small"} style={styles.aiTick} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )
      ) : (
        moreComponent
      )}
    </div>
  );
};

class CheckListViewRow extends React.PureComponent {
  constructor(props) {
    super(props);
  }

  render() {
    const {
      id,
      label,
      options,
      showInsight,
      minOptionCount,
      isEvaluation,
      toolEvaluationResponses,
      header,
      updateEvaluationCell,
      isEvaluatedByStudent,
      insights,
      insightColor,
      userType,
      t,
      toolStyles = {},
      evaluationCellRefs,
      index,
      isLastRow,
      showRevampedEvaluationUI,
      isSharedWithStudent,
      onAcceptAIEvaluation,
      isAiEvaluationEnabled,
      showOnlyDefaultCursor,
      isGeneratingAIResponse,
    } = this.props;

    const tableTextClass = classNames({
      [classes.tableHeader]: header,
      [classes.tableText]: !header,
    });

    const optionHeaderClass = classNames({
      [classes.optionHeader]: true,
      [classes.showInsight]: showInsight,
    });

    const headerContainerClass = classNames({
      [classes.headerOptionContainer]: true,
      [classes.insightsHeaderOptionContainer]: showInsight,
    });

    const {
      containerStyle = {},
      labelContainerStyle = {},
      optionHeaderStyle = {},
      tableTextStyle = {},
      rowStyle = {},
      optionContainerStyle = {},
      topLeftCellStyle = {},
      topRightCellStyle = {},
      bottomLeftCellStyle = {},
      bottomRightCellStyle = {},
      firstColumnTextStyle = {},
      headerRowStyle = {},
      optionHeaderContainerStyle = {},
    } = toolStyles || {};

    return (
      <div className={classes.container} style={containerStyle}>
        <div
          className={classes.row}
          style={{ ...rowStyle, ...(header ? headerRowStyle : {}) }}
        >
          <div
            className={classes.labelContainer}
            style={
              header
                ? {
                    ...styles.headerStyle,
                    ...labelContainerStyle,
                    ...topLeftCellStyle,
                  }
                : {
                    ...labelContainerStyle,
                    ...(isLastRow ? bottomLeftCellStyle : {}),
                  }
            }
          >
            <div
              value={label}
              disabled={true}
              placeholder={t("unitPlan:type_criteria_here")}
              className={tableTextClass}
              style={{
                ...tableTextStyle,
                ...(header ? firstColumnTextStyle : {}),
              }}
            >
              {label}
            </div>
          </div>

          <div className={classes.optionContainer} style={optionContainerStyle}>
            {header ? (
              <div
                className={headerContainerClass}
                style={optionHeaderContainerStyle}
              >
                {_.map(options, (option, index) => {
                  const isTopRightCell = index == _.size(options) - 1;

                  return (
                    <div
                      key={option.id}
                      className={optionHeaderClass}
                      style={{
                        ...optionHeaderStyle,
                        ...(isTopRightCell ? topRightCellStyle : {}),
                      }}
                    >
                      {(showInsight || index == 0) && (
                        <div className={classes.tableheader}>
                          {showInsight
                            ? option.label
                            : t("assessmentEvaluation:options")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              _.map(options, (item, optionIndex) => {
                const isBottomRightCell =
                  optionIndex == _.size(options) - 1 && isLastRow;

                return (
                  <CheckListOption
                    t={t}
                    insights={insights}
                    showInsight={showInsight}
                    isEvaluatedByStudent={isEvaluatedByStudent}
                    isEvaluation={isEvaluation}
                    updateEvaluationCell={updateEvaluationCell}
                    key={item.id}
                    label={item.label}
                    id={item.id}
                    rowId={id}
                    insightColor={insightColor}
                    optionIndex={optionIndex}
                    minOptionCount={minOptionCount}
                    index={optionIndex}
                    toolEvaluationResponses={toolEvaluationResponses}
                    userType={userType}
                    toolStyles={toolStyles}
                    evaluationCellRefs={evaluationCellRefs}
                    rowIndex={index}
                    isBottomRightCell={isBottomRightCell}
                    bottomRightCellStyle={bottomRightCellStyle}
                    showRevampedEvaluationUI={showRevampedEvaluationUI}
                    isSharedWithStudent={isSharedWithStudent}
                    onAcceptAIEvaluation={onAcceptAIEvaluation}
                    isAiEvaluationEnabled={isAiEvaluationEnabled}
                    showOnlyDefaultCursor={showOnlyDefaultCursor}
                    isGeneratingAIResponse={isGeneratingAIResponse}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  return {
    userType: userInfo.user_type,
  };
};

export default compose(
  I18nHOC({
    resource: ["common", "assessmentEvaluation", "unitPlan"],
  }),
  connect(mapStateToProps)
)(CheckListViewRow);
